/**
 * Ficha de suscripción · Iter52.5
 *
 * Rediseño con cuatro pestañas — Operativa, Negocio, Sistema, Histórico —
 * cabecera común fija, leyenda de colores al pie y separación visual de
 * acciones en dos grupos (Operar / Corregir). Sin `window.prompt`: todas
 * las operaciones abren diálogos propios que muestran situación actual,
 * cambio solicitado, fecha efectiva, consecuencias automáticas y efecto
 * económico antes de confirmar.
 */
import React, { useMemo, useState } from 'react';
import { AlertTriangle, X, RefreshCcw } from 'lucide-react';
import { saasApi } from './api';
import { toast } from 'sonner';

const errText = e => e?.response?.data?.detail || e?.message || 'Error';
const fmtDate = d => d ? String(d).slice(0, 10) : '—';
const money = v => v == null ? '—'
  : new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' })
      .format(Number(v));

// ============================================================================
// Utilidades visuales
// ============================================================================
const TONO = {
  RED: 'bg-red-50 border-red-300',
  AMBER: 'bg-amber-50 border-amber-300',
  WHITE: 'bg-white border-slate-200',
  GREY: 'bg-slate-50 border-slate-200 opacity-70',
};

function TabDot({ tone }) {
  if (!tone) return null;
  const cls = tone === 'RED' ? 'bg-red-500' : tone === 'AMBER' ? 'bg-amber-500' : 'bg-transparent';
  return <span className={`inline-block w-2 h-2 rounded-full ml-1 ${cls}`} />;
}

function FieldCard({ label, value, k, bloqueantes = [], necesarios = [], mono = false, testid, ausente = false, extra = null }) {
  const isBloqueante = k && bloqueantes.includes(k);
  const isNecesario = k && !isBloqueante && necesarios.includes(k);
  const isPendiente = !isBloqueante && !isNecesario && ausente;
  const tone = isBloqueante ? 'RED' : isNecesario ? 'AMBER' : isPendiente ? 'GREY' : 'WHITE';
  const shown = value == null || value === '' ? '—' : value;
  return (
    <div
      data-testid={testid}
      data-field={k || undefined}
      data-tone={tone.toLowerCase()}
      className={`border rounded-xl p-3 ${TONO[tone]}`}
    >
      <p className="text-[11px] uppercase tracking-wide text-slate-500 font-bold">{label}</p>
      <p className={`font-semibold mt-1 text-sm ${mono ? 'font-mono' : ''}`}>{shown}</p>
      {extra}
    </div>
  );
}

const _ddmmyyyy = d => { const s = String(d || '').slice(0, 10); if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return s || '—'; const [y, m, dd] = s.split('-'); return `${dd}/${m}/${y}`; };

// Iter52.66 · E2 · Línea «Próximo precio» (solo pantalla). Mismo criterio
// y colores que la columna «Próximo precio» de la lista (52.65 · D8):
// ▲ sube en rosa, ▼ baja en verde. No se pinta si no hay tramo futuro o
// si ese importe no cambia respecto al de hoy.
function ProximoPrecio({ hoy, proxima, desde, alinearDerecha = false }) {  if (proxima == null || !desde) return null;
  const a = parseFloat(hoy ?? 'NaN');
  const b = parseFloat(proxima);
  if (!isNaN(a) && !isNaN(b) && a === b) return null;
  const sube = !isNaN(a) && !isNaN(b) && b > a;
  const baja = !isNaN(a) && !isNaN(b) && b < a;
  return (
    <p className={`text-[11px] mt-1 font-semibold ${sube ? 'text-rose-700' : baja ? 'text-emerald-700' : 'text-slate-500'} ${alinearDerecha ? 'text-right' : ''}`}>
      Próximo precio: {sube ? '▲ ' : baja ? '▼ ' : ''}{money(proxima)} · desde {_ddmmyyyy(desde)}
    </p>
  );
}

function Leyenda() {
  return (
    <div data-testid="ficha-leyenda" className="border-t bg-white px-5 py-3 text-[11px] text-slate-600 flex flex-wrap gap-4 items-center">
      <span className="font-bold uppercase tracking-wide">Leyenda</span>
      <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded bg-red-100 border border-red-300"/>Bloqueante para facturar</span>
      <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded bg-amber-100 border border-amber-300"/>Necesario y correcto</span>
      <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded bg-white border border-slate-200"/>Normal</span>
      <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded bg-slate-100 border border-slate-200"/>Pendiente no bloqueante</span>
    </div>
  );
}

const Badge = ({ children, tone = 'slate' }) => {
  const map = { green: 'bg-emerald-100 text-emerald-800 border-emerald-300', orange: 'bg-orange-100 text-orange-800 border-orange-300', blue: 'bg-blue-100 text-blue-800 border-blue-300', slate: 'bg-slate-100 text-slate-700 border-slate-300', red: 'bg-red-100 text-red-800 border-red-300' };
  return <span className={`inline-block text-[11px] font-bold uppercase tracking-wide border px-2 py-0.5 rounded ${map[tone]||map.slate}`}>{children}</span>;
};
const statusTone = s => ({ ACTIVA: 'green', DESPLEGADO: 'green', SUSPENDIDA: 'orange', PIPELINE: 'blue', BAJA: 'slate', MUERTO: 'red' }[s] || 'slate');

// ============================================================================
// Iter52.26 · Edición en línea (Rama 15) · Campos directos, sin modal.
// ---------------------------------------------------------------------------
// Un solo campo por edición: click en "Editar" → input + textarea de motivo
// (mín. 10 caracteres) → Guardar. Sin diálogo modal, sin preview. El backend
// aplica validaciones canónicas y auditoría automática. Los campos que
// implican consecuencias (precio, baja, fechas, cliente_facturacion_id) NO
// usan este componente: siguen con su diálogo específico.
// ============================================================================
function InlineEditRow({ label, value, save, kind = 'text', options, placeholder, mono = false, testid, hint }) {
  const [editing, setEditing] = useState(false);
  const [nuevo, setNuevo] = useState(value ?? '');
  const [motivo, setMotivo] = useState('');
  const [busy, setBusy] = useState(false);
  React.useEffect(() => { if (editing) { setNuevo(value ?? ''); setMotivo(''); } }, [editing, value]);
  const disabled = busy || motivo.trim().length < 10 || String(nuevo ?? '') === String(value ?? '');
  const commit = async () => {
    setBusy(true);
    try {
      const v = (nuevo === '' && kind !== 'text' && kind !== 'textarea') ? null : nuevo;
      await save(v, motivo.trim());
      toast.success(`${label} actualizado`);
      setEditing(false);
    } catch (e) { toast.error(errText(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="border rounded-xl p-3 bg-white" data-testid={testid}>
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-[10px] uppercase text-slate-500 font-bold tracking-wide">{label}</p>
        {!editing && <button className="text-[11px] text-sky-700 hover:underline" onClick={() => setEditing(true)} data-testid={testid ? `${testid}-edit` : undefined}>Editar</button>}
      </div>
      {!editing ? (
        <p className={`text-sm mt-1 ${mono ? 'font-mono' : ''} ${value == null || value === '' ? 'text-slate-400' : ''}`}>{value == null || value === '' ? '—' : value}</p>
      ) : (
        <div className="mt-2 space-y-2">
          {kind === 'textarea'
            ? <textarea className="input text-sm min-h-[70px]" value={nuevo || ''} onChange={e => setNuevo(e.target.value)} placeholder={placeholder} data-testid={testid ? `${testid}-input` : undefined}/>
            : kind === 'select'
              ? <select className="input text-sm" value={nuevo || ''} onChange={e => setNuevo(e.target.value)} data-testid={testid ? `${testid}-input` : undefined}>
                  <option value="">— sin valor —</option>
                  {(options || []).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              : <input className={`input text-sm ${mono ? 'font-mono' : ''}`} value={nuevo || ''} onChange={e => setNuevo(e.target.value)} placeholder={placeholder} data-testid={testid ? `${testid}-input` : undefined}/>}
          {hint && <p className="text-[11px] text-slate-500">{hint}</p>}
          <textarea className="input text-xs min-h-[50px]" value={motivo} onChange={e => setMotivo(e.target.value)} placeholder={`Motivo obligatorio (mín. 10 · ${motivo.length}/10)`} data-testid={testid ? `${testid}-motivo` : undefined}/>
          <div className="flex gap-2 justify-end">
            <button className="text-[11px] text-slate-500 hover:underline" onClick={() => setEditing(false)} disabled={busy}>Cancelar</button>
            <button className="btn-primary text-xs px-2 py-1" onClick={commit} disabled={disabled} data-testid={testid ? `${testid}-save` : undefined}>{busy ? 'Guardando…' : 'Guardar'}</button>
          </div>
        </div>
      )}
    </div>
  );
}



// ============================================================================
// Diálogo genérico
// ============================================================================
export function Dialog({ title, onClose, children, testid = 'ficha-dialog' }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" data-testid={testid}>
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-5 py-3 border-b flex justify-between items-center">
          <h3 className="font-black text-lg">{title}</h3>
          <button data-testid={`${testid}-cerrar`} onClick={onClose} className="text-slate-500 hover:text-slate-900"><X className="w-5 h-5"/></button>
        </div>
        <div className="p-5 overflow-auto space-y-4 text-sm">{children}</div>
      </div>
    </div>
  );
}

function Row({ label, value, bold = false }) {
  return (
    <div className="flex justify-between gap-3 py-1">
      <span className="text-slate-500">{label}</span>
      <span className={`text-right ${bold ? 'font-bold' : ''}`}>{value}</span>
    </div>
  );
}

// ============================================================================
// Diálogos de operaciones
// ============================================================================
export function OperarDialog({ open, kind, selected, onCancel, onDone }) {
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
  const [motivo, setMotivo] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);

  React.useEffect(() => {
    if (!open) return;
    setFecha(new Date().toISOString().slice(0, 10));
    setMotivo('');
    setPreview(null);
  }, [open, kind, selected?.id]);

  React.useEffect(() => {
    if (!open || !selected || !fecha) return;
    let cancel = false;
    (async () => {
      setLoadingPreview(true);
      try {
        const r = await saasApi.post(`/saas/suscripciones/${selected.id}/${kind}/preview`, { fecha_efectiva: fecha });
        if (!cancel) setPreview(r.data);
      } catch (e) {
        if (!cancel) { setPreview(null); toast.error(errText(e)); }
      } finally { if (!cancel) setLoadingPreview(false); }
    })();
    return () => { cancel = true; };
  }, [open, kind, selected?.id, fecha]);

  if (!open) return null;

  const titleMap = { suspender: 'Suspender suscripción', reactivar: 'Reactivar suscripción', baja: 'Dar de baja' };
  const isBaja = kind === 'baja';
  const consecuenciasMin = {
    suspender: ['Aplica la tarifa suspendida a partir de la fecha efectiva.', 'La suscripción pasa a estado SUSPENDIDA.'],
    reactivar: ['La suscripción vuelve a estado ACTIVA.', 'Se retoma la tarifa activa vigente.'],
    baja: ['Cierra la suscripción con fecha_fin_cobro = último día del mes de baja.', 'El dispositivo asociado pasa a MUERTO (cascada automática auditada).', 'Deja de facturar el mes siguiente.']
  }[kind] || [];
  const canApply = motivo.trim().length >= 5 && !busy && !loadingPreview;
  const bloqueo = preview?.requiere_ajuste ? `La fecha afecta a un mes cerrado. Se generará una prefactura de AJUSTE independiente.` : null;

  const apply = async () => {
    setBusy(true);
    try {
      await saasApi.post(`/saas/suscripciones/${selected.id}/${kind}`, { fecha_efectiva: fecha, motivo: motivo.trim() });
      toast.success(preview?.requiere_ajuste ? 'Aplicado. Se ha generado un ajuste.' : 'Aplicado.');
      onDone();
    } catch (e) { toast.error(errText(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog title={titleMap[kind]} onClose={onCancel} testid={`dialog-${kind}`}>
      <section className="bg-slate-50 border rounded-xl p-3">
        <p className="font-bold text-xs uppercase text-slate-500 mb-1">Situación actual</p>
        <Row label="Estado suscripción" value={selected.estado} />
        <Row label="Estado dispositivo" value={selected.dispositivo?.estado || '—'} />
        <Row label="Tarifa activa" value={money(selected.tarifa_activa_hoy ?? selected.precio_actual?.tarifa_activa)} />
        <ProximoPrecio alinearDerecha hoy={selected.tarifa_activa_hoy} proxima={selected.tarifa_activa_proxima} desde={selected.vigente_desde_proxima} />
        <Row label="Fecha fin cobro" value={fmtDate(selected.fecha_fin_cobro)} />
      </section>

      <section className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
        <p className="font-bold text-xs uppercase text-emerald-800 mb-1">Cambio solicitado</p>
        <Row label="Operación" value={titleMap[kind].toUpperCase()} bold />
        <label className="flex justify-between gap-3 py-1 items-center">
          <span className="text-slate-500">Fecha efectiva</span>
          <input data-testid={`dialog-${kind}-fecha`} type="date" className="input w-40" value={fecha} onChange={e => setFecha(e.target.value)} />
        </label>
      </section>

      <section className="bg-amber-50 border border-amber-200 rounded-xl p-3">
        <p className="font-bold text-xs uppercase text-amber-800 mb-1">Consecuencias automáticas</p>
        <ul className="list-disc list-inside space-y-1 text-slate-700">
          {consecuenciasMin.map((c, i) => <li key={i}>{c}</li>)}
          {preview?.fecha_fin_cobro && isBaja && <li><b>fecha_fin_cobro</b> = {fmtDate(preview.fecha_fin_cobro)}</li>}
        </ul>
      </section>

      {bloqueo && (
        <section className="bg-red-50 border border-red-300 rounded-xl p-3 text-red-800">
          <p className="font-bold mb-1">Efecto económico · prefactura cerrada</p>
          <p>{bloqueo}</p>
        </section>
      )}

      {isBaja && fecha && (() => {
        // Iter52.53 · E4 · Aviso condicional según modalidad y mes.
        const f = new Date(`${fecha}T00:00:00`);
        const ultimoDiaMes = new Date(f.getFullYear(), f.getMonth() + 1, 0);
        const proxMes = new Date(f.getFullYear(), f.getMonth() + 1, 1);
        const dd = String(ultimoDiaMes.getDate()).padStart(2, '0');
        const mm = String(ultimoDiaMes.getMonth() + 1).padStart(2, '0');
        const pxMM = String(proxMes.getMonth() + 1).padStart(2, '0');
        // Si la sub factura anticipado y ese mes YA se cerró la factura,
        // no habrá abono. En caso contrario, se factura hasta fin de mes.
        const modalidad = (selected.cliente_facturacion?.modalidad_facturacion || 'ANTICIPADA').toUpperCase();
        const factMesActualCerrada = preview?.mes_actual_ya_facturado === true;
        const mensaje = factMesActualCerrada
          ? `Ese mes ya se facturó entero, sin abono. La suscripción cerrará con fecha_fin_cobro = ${dd}/${mm}.`
          : `Se facturará hasta el ${dd}/${mm} (último día del mes); no aparecerá en la factura del 01/${pxMM} siguiente.`;
        return <section data-testid="dialog-baja-aviso-condicional" className="bg-sky-50 border border-sky-200 rounded-xl p-3 text-sky-900 text-sm">
          <p>{mensaje}</p>
          {modalidad === 'VENCIDA' && <p className="text-xs mt-1 text-sky-700">Cliente con modalidad VENCIDA: el mes en curso se factura al mes siguiente.</p>}
        </section>;
      })()}

      <section>
        <label className="text-xs font-bold text-slate-600 block mb-1.5">Motivo obligatorio (mín. 5 caracteres · {motivo.length}/5)</label>
        <textarea data-testid={`dialog-${kind}-motivo`} className="input min-h-[70px]" value={motivo} onChange={e => setMotivo(e.target.value)} placeholder="Explica el motivo de la operación (queda auditado)" />
      </section>

      <div className="flex gap-2 justify-end pt-2">
        <button data-testid={`dialog-${kind}-cancelar`} className="btn-secondary" onClick={onCancel} disabled={busy}>Cancelar</button>
        <button data-testid={`dialog-${kind}-confirmar`} className={isBaja ? 'btn-danger' : 'btn-primary'} onClick={apply} disabled={!canApply}>
          {busy ? 'Aplicando…' : `Confirmar ${titleMap[kind].toLowerCase()}`}
        </button>
      </div>
    </Dialog>
  );
}

export function ReactivarSensibleDialog({ open, selected, onCancel, onDone }) {
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
  const [motivo, setMotivo] = useState('');
  const [confirmoTexto, setConfirmoTexto] = useState('');
  const [busy, setBusy] = useState(false);
  React.useEffect(() => { if (open) { setFecha(new Date().toISOString().slice(0, 10)); setMotivo(''); setConfirmoTexto(''); } }, [open, selected?.id]);
  if (!open) return null;
  const idPrincipal = selected.dispositivo?.matricula || selected.dispositivo?.serial_geotab || selected.dispositivo?.id_pack_origen || selected.id;
  const canApply = motivo.trim().length >= 10 && confirmoTexto.trim().toUpperCase() === 'REACTIVAR' && !busy;
  const apply = async () => {
    setBusy(true);
    try {
      await saasApi.post(`/saas/suscripciones/${selected.id}/reactivar`, { fecha_efectiva: fecha, motivo: motivo.trim() });
      toast.success('Reactivación aplicada (auditada como sensible).');
      onDone();
    } catch (e) { toast.error(errText(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog title="Reactivar desde BAJA (sensible)" onClose={onCancel} testid="dialog-reactivar-sensible">
      <section className="bg-red-50 border border-red-300 rounded-xl p-3 text-red-900">
        <p className="font-bold text-xs uppercase mb-1">Operación sensible</p>
        <p>Vas a reactivar la suscripción <b>{idPrincipal}</b>, que actualmente está en BAJA. La reactivación queda auditada como <b>REACTIVACION</b>, no altera la facturación de meses ya cerrados y reabre la posibilidad de emisión desde la fecha efectiva.</p>
      </section>
      <label className="flex justify-between gap-3 items-center">
        <span className="text-slate-500">Fecha efectiva</span>
        <input data-testid="dialog-reactivar-sensible-fecha" type="date" className="input w-40" value={fecha} onChange={e => setFecha(e.target.value)} />
      </label>
      <div>
        <label className="text-xs font-bold text-slate-600 block mb-1.5">Motivo obligatorio (mín. 10 caracteres · {motivo.length}/10)</label>
        <textarea data-testid="dialog-reactivar-sensible-motivo" className="input min-h-[80px]" value={motivo} onChange={e => setMotivo(e.target.value)} placeholder="Detalla por qué se reactiva una suscripción dada de baja" />
      </div>
      <div>
        <label className="text-xs font-bold text-slate-600 block mb-1.5">Escribe REACTIVAR para confirmar</label>
        <input data-testid="dialog-reactivar-sensible-confirmacion" className="input" value={confirmoTexto} onChange={e => setConfirmoTexto(e.target.value)} placeholder="REACTIVAR" />
      </div>
      <div className="flex gap-2 justify-end pt-2">
        <button className="btn-secondary" onClick={onCancel} disabled={busy}>Cancelar</button>
        <button data-testid="dialog-reactivar-sensible-confirmar" className="btn-primary bg-amber-600 hover:bg-amber-700" onClick={apply} disabled={!canApply}>Reactivar</button>
      </div>
    </Dialog>
  );
}

export function RenovacionFueraAppDialog({ selected, onCancel, onDone }) {
  // Iter52.53 · A4 · Registrar renovación facturada fuera de la app.
  // Crea el periodo siguiente (PROGRAMADO o ACTIVO según fecha) con
  // marca `facturado_en=FUERA_APP`, sin generar línea.
  // Iter52.54 · Punto 6 · El detalle de la suscripción expone el periodo
  // en `periodo` (no `periodo_activo`) y su fin canónico en
  // `fecha_fin_efectiva` (los PREPAGO migrados no guardan `fecha_fin`).
  const perAct = selected?.periodo || selected?.periodo_activo;
  const finRaw = perAct?.fecha_fin_efectiva || perAct?.fecha_fin;
  const finAct = finRaw ? String(finRaw).slice(0, 10) : null;
  const defMeses = perAct?.meses_prepagados || '';
  const _addDays = (iso, n) => {
    if (!iso) return '';
    const d = new Date(`${iso}T00:00:00`);
    d.setDate(d.getDate() + n);
    return d.toISOString().slice(0, 10);
  };
  const [meses, setMeses] = React.useState(defMeses);
  const [fechaInicio, setFechaInicio] = React.useState(_addDays(finAct, 1));
  const [motivo, setMotivo] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const canApply = String(motivo).trim().length >= 10 && Number(meses) > 0 && !busy;
  const apply = async () => {
    setBusy(true);
    try {
      const body = {
        meses_prepagados: Number(meses),
        motivo: motivo.trim(),
        ...(fechaInicio ? { fecha_inicio: fechaInicio } : {}),
      };
      const r = await saasApi.post(`/saas/prefacturas/renovacion-fuera-app/${selected.id}`, body);
      toast.success(r.data?.estado === 'PROGRAMADO'
        ? 'Renovación registrada como PROGRAMADO'
        : 'Renovación registrada como ACTIVO');
      onDone && onDone();
    } catch (e) { toast.error(errText(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog title="Registrar renovación facturada fuera de la app" onClose={onCancel} testid="dialog-renovacion-fuera-app">
      <section className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-amber-900">
        <p className="text-sm">Crea el periodo siguiente sin generar línea en la prefactura. Se usa cuando la renovación se factura por fuera. Queda auditado y marcado como <code>FUERA_APP</code>.</p>
      </section>
      <div className="grid md:grid-cols-2 gap-3">
        <label className="block">
          <span className="text-xs text-slate-500">Meses prepagados{defMeses ? ` (periodo activo: ${defMeses} m)` : ''}</span>
          <input data-testid="dialog-renov-fuera-meses" type="number" min="1" step="1" className="input" value={meses} onChange={e => setMeses(e.target.value)} />
        </label>
        <label className="block">
          <span className="text-xs text-slate-500">Fecha inicio{finAct ? ` (por defecto ${finAct} + 1)` : ' (opcional)'}</span>
          <input data-testid="dialog-renov-fuera-fecha" type="date" className="input" value={fechaInicio} onChange={e => setFechaInicio(e.target.value)} />
        </label>
      </div>
      <div>
        <label className="text-xs font-bold text-slate-600 block mb-1.5">Motivo obligatorio (mín. 10 caracteres · {motivo.length}/10)</label>
        <textarea data-testid="dialog-renov-fuera-motivo" className="input min-h-[80px]" value={motivo} onChange={e => setMotivo(e.target.value)} placeholder="Ej.: renovación facturada fuera de la app por acuerdo previo en junio 2026 (TOLOSA)" />
      </div>
      <div className="flex gap-2 justify-end pt-2">
        <button className="btn-secondary" onClick={onCancel} disabled={busy}>Cancelar</button>
        <button data-testid="dialog-renov-fuera-confirmar" className="btn-primary" onClick={apply} disabled={!canApply}>Registrar</button>
      </div>
    </Dialog>
  );
}


export function CambiarPrecioDialog({ open, selected, onCancel, onDone }) {
  const [activa, setActiva] = useState('');
  const [suspendida, setSuspendida] = useState('');
  const [motivo, setMotivo] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  React.useEffect(() => { if (open) { setActiva(''); setSuspendida(''); setMotivo(''); setPreview(null); } }, [open, selected?.id]);
  if (!open) return null;
  const canPreview = (activa !== '' || suspendida !== '') && motivo.trim().length >= 5 && !busy;
  const doPreview = async () => {
    setBusy(true);
    try {
      const payload = { tarifa_activa: activa === '' ? null : Number(activa), tarifa_suspendida: suspendida === '' ? null : Number(suspendida), motivo: motivo.trim() };
      const r = await saasApi.post(`/saas/suscripciones/${selected.id}/precio/preview`, payload);
      setPreview(r.data);
    } catch (e) { toast.error(errText(e)); }
    finally { setBusy(false); }
  };
  const apply = async () => {
    setBusy(true);
    try {
      const payload = { tarifa_activa: activa === '' ? null : Number(activa), tarifa_suspendida: suspendida === '' ? null : Number(suspendida), motivo: motivo.trim() };
      await saasApi.post(`/saas/suscripciones/${selected.id}/precio`, payload);
      toast.success('Precio programado.');
      onDone();
    } catch (e) { toast.error(errText(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog title="Cambiar precio" onClose={onCancel} testid="dialog-cambiar-precio">
      <section className="bg-slate-50 border rounded-xl p-3">
        <p className="font-bold text-xs uppercase text-slate-500 mb-1">Situación actual</p>
        <Row label="Tarifa activa" value={money(selected.tarifa_activa_hoy ?? selected.precio_actual?.tarifa_activa)} />
        <ProximoPrecio alinearDerecha hoy={selected.tarifa_activa_hoy} proxima={selected.tarifa_activa_proxima} desde={selected.vigente_desde_proxima} />
        <Row label="Tarifa suspendida" value={money(selected.tarifa_suspendida_hoy ?? selected.precio_actual?.tarifa_suspendida)} />
        <ProximoPrecio alinearDerecha hoy={selected.tarifa_suspendida_hoy} proxima={selected.tarifa_suspendida_proxima} desde={selected.vigente_desde_proxima} />
      </section>
      <section className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 space-y-2">
        <p className="font-bold text-xs uppercase text-emerald-800 mb-1">Cambio solicitado</p>
        <label className="flex justify-between gap-3 items-center"><span className="text-slate-500">Nueva tarifa activa</span><input data-testid="dialog-precio-activa" type="number" step="0.01" className="input w-40" value={activa} onChange={e => setActiva(e.target.value)} placeholder="conservar"/></label>
        <label className="flex justify-between gap-3 items-center"><span className="text-slate-500">Nueva tarifa suspendida</span><input data-testid="dialog-precio-suspendida" type="number" step="0.01" className="input w-40" value={suspendida} onChange={e => setSuspendida(e.target.value)} placeholder="conservar"/></label>
      </section>
      <section className="bg-amber-50 border border-amber-200 rounded-xl p-3">
        <p className="font-bold text-xs uppercase text-amber-800 mb-1">Consecuencias automáticas</p>
        <ul className="list-disc list-inside space-y-1 text-slate-700">
          <li>Se abre un nuevo tramo de precio con vigencia <b>día 1 del mes siguiente</b> al cambio.</li>
          <li>El precio vigente actual se cierra a fin del mes en curso. Sin prorrateo intra-mes.</li>
          {preview?.vigente_desde && <li><b>Vigente desde</b> {fmtDate(preview.vigente_desde)}</li>}
        </ul>
      </section>
      <div>
        <label className="text-xs font-bold text-slate-600 block mb-1.5">Motivo obligatorio (mín. 5 caracteres · {motivo.length}/5)</label>
        <textarea data-testid="dialog-precio-motivo" className="input min-h-[70px]" value={motivo} onChange={e => setMotivo(e.target.value)} placeholder="Detalla el motivo del cambio de precio" />
      </div>
      <div className="flex gap-2 justify-end pt-2">
        <button className="btn-secondary" onClick={onCancel} disabled={busy}>Cancelar</button>
        <button data-testid="dialog-precio-preview" className="btn-secondary" onClick={doPreview} disabled={!canPreview}>Ver preview</button>
        <button data-testid="dialog-precio-confirmar" className="btn-primary" onClick={apply} disabled={!canPreview}>Confirmar cambio</button>
      </div>
    </Dialog>
  );
}

// ============================================================================
// Iter52.22 · Asignación INICIAL de precio (corrección de dato de migración)
// ----------------------------------------------------------------------------
// Operación distinta a `Cambiar precio`. Se usa cuando la suscripción está
// bloqueada por SIN_PRECIO_VIGENTE / PRECIO_ACTIVA_CERO: rellena el hueco
// de precio desde el inicio de cobro (o desde el mes siguiente al último
// cerrado). No es un cambio comercial hacia el futuro.
// ============================================================================
export function AsignarPrecioInicialDialog({ open, selected, onCancel, onDone }) {
  const [activa, setActiva] = useState('');
  const [suspendida, setSuspendida] = useState('');
  const [motivo, setMotivo] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  React.useEffect(() => {
    if (open) { setActiva(''); setSuspendida(''); setMotivo(''); setPreview(null); }
  }, [open, selected?.id]);
  if (!open) return null;
  const canApply = activa !== '' && suspendida !== '' && motivo.trim().length >= 5 && !busy;
  const doPreview = async () => {
    setBusy(true);
    try {
      const payload = { tarifa_activa: Number(activa), tarifa_suspendida: Number(suspendida), motivo: motivo.trim() };
      const r = await saasApi.post(`/saas/suscripciones/${selected.id}/precio-inicial/preview`, payload);
      setPreview(r.data);
    } catch (e) { toast.error(errText(e)); }
    finally { setBusy(false); }
  };
  const apply = async () => {
    setBusy(true);
    try {
      const payload = { tarifa_activa: Number(activa), tarifa_suspendida: Number(suspendida), motivo: motivo.trim() };
      await saasApi.post(`/saas/suscripciones/${selected.id}/precio-inicial`, payload);
      toast.success('Precio inicial asignado. La suscripción ya no está bloqueada por precio.');
      onDone();
    } catch (e) { toast.error(errText(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog title="Asignar precio inicial" onClose={onCancel} testid="dialog-precio-inicial">
      <section className="bg-slate-50 border rounded-xl p-3">
        <p className="font-bold text-xs uppercase text-slate-500 mb-1">Situación actual</p>
        <Row label="Tarifa activa" value={money(selected.tarifa_activa_hoy ?? selected.precio_actual?.tarifa_activa)} />
        <ProximoPrecio alinearDerecha hoy={selected.tarifa_activa_hoy} proxima={selected.tarifa_activa_proxima} desde={selected.vigente_desde_proxima} />
        <Row label="Tarifa suspendida" value={money(selected.tarifa_suspendida_hoy ?? selected.precio_actual?.tarifa_suspendida)} />
        <ProximoPrecio alinearDerecha hoy={selected.tarifa_suspendida_hoy} proxima={selected.tarifa_suspendida_proxima} desde={selected.vigente_desde_proxima} />
        <Row label="Inicio de cobro" value={fmtDate(selected.fecha_inicio_cobro)} />
      </section>
      <section className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 space-y-2">
        <p className="font-bold text-xs uppercase text-emerald-800 mb-1">Precio inicial a asignar</p>
        <label className="flex justify-between gap-3 items-center"><span className="text-slate-500">Tarifa activa</span><input data-testid="dialog-precio-inicial-activa" type="number" step="0.01" className="input w-40" value={activa} onChange={e => setActiva(e.target.value)} placeholder="p.ej. 13.96"/></label>
        <label className="flex justify-between gap-3 items-center"><span className="text-slate-500">Tarifa suspendida</span><input data-testid="dialog-precio-inicial-suspendida" type="number" step="0.01" className="input w-40" value={suspendida} onChange={e => setSuspendida(e.target.value)} placeholder="p.ej. 5.00"/></label>
      </section>
      <section className="bg-amber-50 border border-amber-200 rounded-xl p-3">
        <p className="font-bold text-xs uppercase text-amber-800 mb-1">Regla de asignación inicial</p>
        <ul className="list-disc list-inside space-y-1 text-slate-700">
          <li>Rellena el hueco de precio <b>desde el inicio de cobro</b> de la suscripción.</li>
          <li>Los meses ya <b>cerrados nunca se reescriben</b>: la vigencia arranca en el mes siguiente al último cerrado si lo hay.</li>
          <li>Si necesitas ajuste económico sobre un mes cerrado, va por el circuito de <b>ajustes</b> (aparte).</li>
          {preview?.vigente_desde && <li><b>Vigente desde</b> {fmtDate(preview.vigente_desde)}{preview.ultimo_mes_cerrado && <> (mes siguiente al último cerrado: <b>{preview.ultimo_mes_cerrado}</b>)</>}</li>}
        </ul>
      </section>
      <div>
        <label className="text-xs font-bold text-slate-600 block mb-1.5">Motivo obligatorio (mín. 5 caracteres · {motivo.length}/5)</label>
        <textarea data-testid="dialog-precio-inicial-motivo" className="input min-h-[70px]" value={motivo} onChange={e => setMotivo(e.target.value)} placeholder="Detalla el motivo (p.ej. dato faltante en migración MAESTRO)" />
      </div>
      <div className="flex gap-2 justify-end pt-2">
        <button className="btn-secondary" onClick={onCancel} disabled={busy}>Cancelar</button>
        <button data-testid="dialog-precio-inicial-preview" className="btn-secondary" onClick={doPreview} disabled={!canApply}>Ver preview</button>
        <button data-testid="dialog-precio-inicial-confirmar" className="btn-primary" onClick={apply} disabled={!canApply}>Asignar precio inicial</button>
      </div>
    </Dialog>
  );
}


export function ReconstruirPeriodoDialog({ open, selected, onCancel, onDone }) {
  const diag = selected?.reconstruccion_periodo;
  const [tipo, setTipo] = useState(diag?.valores?.tipo || 'MENSUAL');
  const [meses, setMeses] = useState(diag?.valores?.meses_prepagados || '');
  const [fechaInicio, setFechaInicio] = useState(diag?.valores?.fecha_inicio || selected?.fecha_inicio_cobro?.slice?.(0, 10) || '');
  const [motivo, setMotivo] = useState('');
  const [busy, setBusy] = useState(false);
  React.useEffect(() => {
    if (!open) return;
    setTipo(diag?.valores?.tipo || 'MENSUAL');
    setMeses(diag?.valores?.meses_prepagados || '');
    setFechaInicio(diag?.valores?.fecha_inicio || selected?.fecha_inicio_cobro?.slice?.(0, 10) || '');
    setMotivo('');
  }, [open, selected?.id]);
  if (!open) return null;
  const canApply = motivo.trim().length >= 10 && fechaInicio && (tipo === 'MENSUAL' || (tipo === 'PREPAGO' && Number(meses) >= 1)) && !busy;
  const apply = async () => {
    setBusy(true);
    try {
      const payload = { tipo, motivo: motivo.trim(), fecha_inicio: fechaInicio };
      if (tipo === 'PREPAGO') payload.meses_prepagados = Number(meses);
      await saasApi.post(`/saas/suscripciones/${selected.id}/corregir-periodo`, payload);
      toast.success('Período reconstruido correctamente.');
      onDone();
    } catch (e) { toast.error(errText(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog title="Reconstruir período de cobro" onClose={onCancel} testid="dialog-reconstruir-periodo">
      <section className="bg-slate-50 border rounded-xl p-3">
        <p className="font-bold text-xs uppercase text-slate-500 mb-1">Situación actual</p>
        <Row label="Estado suscripción" value={selected.estado} />
        <Row label="Período activo" value="No existe (huérfana)" />
        <Row label="Fecha inicio cobro (BBDD)" value={fmtDate(selected.fecha_inicio_cobro)} />
      </section>
      {!diag?.reconstruible && (
        <section className="bg-red-50 border border-red-300 rounded-xl p-3 text-red-900">
          <p className="font-bold text-xs uppercase mb-1">No reconstruible</p>
          <p><b>{diag?.motivo_codigo || '—'}</b> · {diag?.motivo || 'Falta información obligatoria.'}</p>
        </section>
      )}
      <section className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 space-y-2">
        <p className="font-bold text-xs uppercase text-emerald-800 mb-1">Datos para reconstruir</p>
        <label className="flex justify-between gap-3 items-center"><span className="text-slate-500">TIPO</span>
          <select data-testid="dialog-recon-tipo" className="input w-40" value={tipo} onChange={e => setTipo(e.target.value)}>
            <option value="MENSUAL">MENSUAL</option>
            <option value="PREPAGO">PREPAGO</option>
          </select>
        </label>
        {tipo === 'PREPAGO' && (
          <label className="flex justify-between gap-3 items-center"><span className="text-slate-500">Meses prepagados</span>
            <input data-testid="dialog-recon-meses" type="number" min="1" className="input w-40" value={meses} onChange={e => setMeses(e.target.value)} />
          </label>
        )}
        <label className="flex justify-between gap-3 items-center"><span className="text-slate-500">Fecha inicio</span>
          <input data-testid="dialog-recon-fecha" type="date" className="input w-40" value={fechaInicio} onChange={e => setFechaInicio(e.target.value)} />
        </label>
      </section>
      <section className="bg-amber-50 border border-amber-200 rounded-xl p-3">
        <p className="font-bold text-xs uppercase text-amber-800 mb-1">Consecuencias automáticas</p>
        <ul className="list-disc list-inside space-y-1 text-slate-700">
          <li>Se creará un nuevo período activo con los datos indicados.</li>
          <li>Auditoría neutra <b>RECONSTRUCCION_PERIODO_AUSENTE</b>.</li>
          <li>Se actualiza <code>periodo_cobro_id</code> en la suscripción.</li>
          {tipo === 'PREPAGO' && meses && <li>Fecha fin canónica: último día del mes fecha_inicio + {meses} − 1.</li>}
        </ul>
      </section>
      <div>
        <label className="text-xs font-bold text-slate-600 block mb-1.5">Motivo obligatorio (mín. 10 caracteres · {motivo.length}/10)</label>
        <textarea data-testid="dialog-recon-motivo" className="input min-h-[70px]" value={motivo} onChange={e => setMotivo(e.target.value)} placeholder="Ej: la suscripción se creó inicialmente en BAJA y no generó período; se reconstruye ahora que está ACTIVA" />
      </div>
      <div className="flex gap-2 justify-end pt-2">
        <button className="btn-secondary" onClick={onCancel} disabled={busy}>Cancelar</button>
        <button data-testid="dialog-recon-confirmar" className="btn-primary" onClick={apply} disabled={!canApply}>Confirmar reconstrucción</button>
      </div>
    </Dialog>
  );
}

export function CambiarProductoDialog({ open, selected, productos = [], onCancel, onDone }) {
  const s = selected || {};
  const productoActualId = s.producto_saas_id || s.producto_saas?.id;
  const [nuevoProductoId, setNuevoProductoId] = useState('');
  const [fechaEfectiva, setFechaEfectiva] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  });
  const [tarifaActiva, setTarifaActiva] = useState('');
  const [tarifaSuspendida, setTarifaSuspendida] = useState('');
  const [tipoPeriodo, setTipoPeriodo] = useState('MENSUAL');
  const [mesesPrepagados, setMesesPrepagados] = useState('');
  const [motivo, setMotivo] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  React.useEffect(() => {
    if (!open) return;
    setNuevoProductoId('');
    setTarifaActiva(''); setTarifaSuspendida('');
    setTipoPeriodo('MENSUAL'); setMesesPrepagados('');
    setMotivo(''); setPreview(null);
    const d = new Date(); d.setDate(d.getDate() + 1);
    setFechaEfectiva(d.toISOString().slice(0, 10));
  }, [open, selected?.id]);
  if (!open) return null;
  const productosDisponibles = (productos || []).filter(p => (p.id || p.sku) !== productoActualId);
  const productoSel = productos.find(p => (p.id || p.sku) === nuevoProductoId);
  const canPreview = nuevoProductoId && fechaEfectiva && Number(tarifaActiva) > 0
                   && motivo.trim().length >= 5
                   && (tipoPeriodo === 'MENSUAL' || (tipoPeriodo === 'PREPAGO' && Number(mesesPrepagados) >= 1))
                   && !busy;
  const buildPayload = () => ({
    nuevo_producto_saas_id: nuevoProductoId,
    fecha_efectiva: fechaEfectiva,
    nueva_tarifa_activa: Number(tarifaActiva),
    nueva_tarifa_suspendida: tarifaSuspendida === '' ? 0 : Number(tarifaSuspendida),
    nuevo_tipo_periodo: tipoPeriodo,
    nuevos_meses_prepagados: tipoPeriodo === 'PREPAGO' ? Number(mesesPrepagados) : null,
    motivo: motivo.trim(),
  });
  const doPreview = async () => {
    setBusy(true);
    try {
      const r = await saasApi.post(`/saas/suscripciones/${s.id}/cambiar-producto/preview`, buildPayload());
      setPreview(r.data);
    } catch (e) { toast.error(errText(e)); }
    finally { setBusy(false); }
  };
  const apply = async () => {
    setBusy(true);
    try {
      await saasApi.post(`/saas/suscripciones/${s.id}/cambiar-producto`, buildPayload());
      toast.success('Cambio de producto aplicado. Nueva suscripción creada.');
      onDone();
    } catch (e) { toast.error(errText(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog title="Cambiar suscripción · producto SaaS" onClose={onCancel} testid="dialog-cambiar-producto">
      <section className="bg-slate-50 border rounded-xl p-3">
        <p className="font-bold text-xs uppercase text-slate-500 mb-1">Situación actual</p>
        <Row label="Producto actual" value={s.producto_saas?.nombre || s.tipo_servicio_snapshot || '—'} />
        <Row label="Estado" value={s.estado} />
        <Row label="Inicio de cobro" value={fmtDate(s.fecha_inicio_cobro)} />
        <Row label="Tarifa activa" value={money(s.tarifa_activa_hoy ?? s.precio_actual?.tarifa_activa)} />
        <ProximoPrecio alinearDerecha hoy={s.tarifa_activa_hoy} proxima={s.tarifa_activa_proxima} desde={s.vigente_desde_proxima} />
      </section>
      <section className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 space-y-2">
        <p className="font-bold text-xs uppercase text-emerald-800 mb-1">Nuevo producto</p>
        <label className="flex justify-between gap-3 items-center"><span className="text-slate-500">Producto SaaS destino</span>
          <select data-testid="dialog-cambprod-producto" className="input w-64" value={nuevoProductoId} onChange={e => setNuevoProductoId(e.target.value)}>
            <option value="">— selecciona —</option>
            {productosDisponibles.map(p => <option key={p.id || p.sku} value={p.id || p.sku}>{p.sku ? `${p.sku} · ` : ''}{p.nombre || p.descripcion}</option>)}
          </select>
        </label>
        {productoSel?.plan_geotab && <Row label="Plan Geotab asociado" value={productoSel.plan_geotab} />}
        <label className="flex justify-between gap-3 items-center"><span className="text-slate-500">Fecha efectiva</span>
          <input data-testid="dialog-cambprod-fecha" type="date" className="input w-40" value={fechaEfectiva} onChange={e => setFechaEfectiva(e.target.value)} min={new Date().toISOString().slice(0, 10)} />
        </label>
        <label className="flex justify-between gap-3 items-center"><span className="text-slate-500">Nueva tarifa activa (€/mes)</span>
          <input data-testid="dialog-cambprod-tarifa" type="number" step="0.01" min="0.01" className="input w-40" value={tarifaActiva} onChange={e => setTarifaActiva(e.target.value)} />
        </label>
        <label className="flex justify-between gap-3 items-center"><span className="text-slate-500">Nueva tarifa suspendida (€/mes)</span>
          <input data-testid="dialog-cambprod-suspendida" type="number" step="0.01" min="0" className="input w-40" value={tarifaSuspendida} onChange={e => setTarifaSuspendida(e.target.value)} />
        </label>
        <label className="flex justify-between gap-3 items-center"><span className="text-slate-500">Tipo de período</span>
          <select data-testid="dialog-cambprod-tipoperiodo" className="input w-40" value={tipoPeriodo} onChange={e => setTipoPeriodo(e.target.value)}>
            <option value="MENSUAL">MENSUAL</option>
            <option value="PREPAGO">PREPAGO</option>
          </select>
        </label>
        {tipoPeriodo === 'PREPAGO' && <label className="flex justify-between gap-3 items-center"><span className="text-slate-500">Meses prepagados</span>
          <input data-testid="dialog-cambprod-meses" type="number" min="1" max="60" className="input w-40" value={mesesPrepagados} onChange={e => setMesesPrepagados(e.target.value)} />
        </label>}
      </section>
      <section className="bg-amber-50 border border-amber-200 rounded-xl p-3">
        <p className="font-bold text-xs uppercase text-amber-800 mb-1">Consecuencias automáticas</p>
        <ul className="list-disc list-inside space-y-1 text-slate-700">
          <li>La suscripción actual se cierra como <b>BAJA</b> con <code>fecha_fin_cobro = fecha_efectiva − 1</code>.</li>
          <li>Se crea una <b>suscripción nueva ACTIVA</b> con el nuevo producto, mismo dispositivo y clientes.</li>
          <li>La prefactura del mes generará prorrateos automáticos para ambos productos.</li>
          <li>Se registra evento <code>CAMBIO_PRODUCTO_SAAS</code> con trazabilidad origen ↔ destino.</li>
        </ul>
      </section>
      <div>
        <label className="text-xs font-bold text-slate-600 block mb-1.5">Motivo obligatorio (mín. 5 caracteres · {motivo.length}/5)</label>
        <textarea data-testid="dialog-cambprod-motivo" className="input min-h-[70px]" value={motivo} onChange={e => setMotivo(e.target.value)} placeholder="Ej: renegociación de contrato al plan Pro; upgrade solicitado por cliente" />
      </div>
      {preview?.suscripcion_nueva && <section className="bg-white border rounded-xl p-3 text-xs" data-testid="dialog-cambprod-preview">
        <p className="font-bold uppercase text-slate-500 mb-1">Preview</p>
        <Row label="Cierre actual" value={fmtDate(preview.suscripcion_actual?.fecha_fin_propuesta)} />
        <Row label="Inicio nueva" value={fmtDate(preview.suscripcion_nueva?.fecha_inicio_cobro)} />
        <Row label="Producto nuevo" value={preview.suscripcion_nueva?.producto_saas} />
      </section>}
      <div className="flex gap-2 justify-end pt-2">
        <button className="btn-secondary" onClick={onCancel} disabled={busy}>Cancelar</button>
        <button data-testid="dialog-cambprod-preview-btn" className="btn-secondary" onClick={doPreview} disabled={!canPreview}>Ver preview</button>
        <button data-testid="dialog-cambprod-confirmar" className="btn-primary" onClick={apply} disabled={!canPreview}>Confirmar cambio</button>
      </div>
    </Dialog>
  );
}


// ============================================================================
// Ficha principal · 4 pestañas
// ============================================================================
const TABS = [
  { k: 'operativa', label: 'Operativa' },
  { k: 'negocio',  label: 'Negocio' },
  { k: 'sistema',  label: 'Sistema' },
  { k: 'historico', label: 'Histórico' },
];

// Devuelve el tono más crítico de una pestaña, dado un mapa
// {campo -> tono}. Prioridad: RED > AMBER > null.
function domTone(fields, m) {
  let has = null;
  for (const f of fields) {
    const t = m[f];
    if (t === 'RED') return 'RED';
    if (t === 'AMBER') has = 'AMBER';
  }
  return has;
}


// ============================================================================
// Iter52.58 · Rama 28 · Añadir SERVICIO (hotline y similares) a la principal.
// El servicio comparte equipo, ID Geotab y cliente de facturación, y se cobra
// como cualquier otra suscripción.
// ============================================================================
export function AnadirServicioDialog({ selected, onCancel, onDone }) {
  const [productos, setProductos] = useState([]);
  const [f, setF] = useState({
    producto_saas_id: '', tarifa_activa: '', periodo_cobro_tipo: 'MENSUAL',
    meses_prepagados: '', fecha_inicio_cobro: new Date().toISOString().slice(0, 10),
    motivo: '',
  });
  const [busy, setBusy] = useState(false);
  React.useEffect(() => {
    (async () => {
      try {
        const r = await saasApi.get('/saas/servicios/productos');
        setProductos(r.data?.items || []);
      } catch { toast.error('No se pudo cargar el catálogo de servicios'); }
    })();
  }, []);
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const valido = f.producto_saas_id && Number(f.tarifa_activa) >= 0
    && f.tarifa_activa !== '' && f.fecha_inicio_cobro
    && (f.periodo_cobro_tipo !== 'PREPAGO' || Number(f.meses_prepagados) > 0);
  const guardar = async () => {
    setBusy(true);
    try {
      const r = await saasApi.post(`/saas/servicios/${selected.id}`, {
        producto_saas_id: f.producto_saas_id,
        tarifa_activa: Number(f.tarifa_activa),
        periodo_cobro_tipo: f.periodo_cobro_tipo,
        meses_prepagados: f.periodo_cobro_tipo === 'PREPAGO' ? Number(f.meses_prepagados) : null,
        fecha_inicio_cobro: f.fecha_inicio_cobro,
        motivo: f.motivo || null,
      });
      toast.success(`Servicio añadido (${r.data?.estado})`);
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo añadir el servicio');
    } finally { setBusy(false); }
  };
  return (
    <Dialog title="Añadir servicio al equipo" onClose={onCancel} testid="dialog-anadir-servicio">
      <div className="space-y-3 text-sm">
        <div className="text-xs bg-sky-50 border border-sky-200 rounded-lg p-3 text-sky-900">
          Servicios que <b>no van sobre Geotab</b> (hotline y similares). Se cobran como una
          suscripción más, con el mismo ID Geotab del equipo y el mismo cliente de facturación.
          Si el equipo se da de baja, el servicio se da de baja con él.
        </div>
        <label className="block">
          <span className="text-xs font-bold text-slate-600">Producto *</span>
          <select data-testid="servicio-producto" className="input mt-1" value={f.producto_saas_id}
                  onChange={e => {
                    const p = productos.find(x => x.id === e.target.value);
                    set('producto_saas_id', e.target.value);
                    if (p && f.tarifa_activa === '') set('tarifa_activa', String(p.precio ?? ''));
                  }}>
            <option value="">Selecciona…</option>
            {productos.map(p => <option key={p.id} value={p.id}>{p.nombre} · {Number(p.precio || 0).toFixed(2)} €</option>)}
          </select>
          {!productos.length && <span className="text-[11px] text-amber-700">No hay productos marcados como «Va sobre Geotab: No» en el catálogo.</span>}
        </label>
        <label className="block">
          <span className="text-xs font-bold text-slate-600">Precio pactado €/mes *</span>
          <input data-testid="servicio-precio" type="number" step="0.01" className="input mt-1"
                 value={f.tarifa_activa} onChange={e => set('tarifa_activa', e.target.value)} />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs font-bold text-slate-600">Tipo de periodo *</span>
            <select data-testid="servicio-periodo" className="input mt-1" value={f.periodo_cobro_tipo}
                    onChange={e => set('periodo_cobro_tipo', e.target.value)}>
              <option value="MENSUAL">MENSUAL</option>
              <option value="PREPAGO">PREPAGO</option>
            </select>
          </label>
          {f.periodo_cobro_tipo === 'PREPAGO' && <label className="block">
            <span className="text-xs font-bold text-slate-600">Meses prepagados *</span>
            <input data-testid="servicio-meses" type="number" className="input mt-1"
                   value={f.meses_prepagados} onChange={e => set('meses_prepagados', e.target.value)} />
          </label>}
        </div>
        <label className="block">
          <span className="text-xs font-bold text-slate-600">Inicio de cobro *</span>
          <input data-testid="servicio-inicio" type="date" className="input mt-1"
                 value={f.fecha_inicio_cobro} onChange={e => set('fecha_inicio_cobro', e.target.value)} />
        </label>
        <label className="block">
          <span className="text-xs font-bold text-slate-600">Motivo / referencia</span>
          <input data-testid="servicio-motivo" className="input mt-1" value={f.motivo}
                 onChange={e => set('motivo', e.target.value)} placeholder="p. ej. hotline contratada en la renovación 2026" />
        </label>
        <div className="flex gap-2 justify-end pt-1">
          <button className="btn-secondary" onClick={onCancel} disabled={busy}>Cancelar</button>
          <button data-testid="servicio-guardar" className="btn-primary" onClick={guardar} disabled={busy || !valido}>
            {busy ? 'Añadiendo…' : 'Añadir servicio'}
          </button>
        </div>
      </div>
    </Dialog>
  );
}

export default function SubscriptionFicha({ selected, tab, setTab, fromControl, backToControl, onClose, onOperar, onCambiarPrecio, onAsignarPrecioInicial, onCambiarSuscripcion, onReactivarSensible, onRenovacionFueraApp, onCorregirDatos, onCorregirPeriodo, onCorregirEstadoDispositivo, onCorregirDatosDispositivo, onReconstruirPeriodo, onRefresh }) {
  // Iter52.26 · Inline edit · saves atómicos con motivo + auditoría.
  const saveSusField = async (campo, valor, motivo) => {
    await saasApi.post(`/saas/suscripciones/${selected.id}/corregir-datos`,
      { campos: { [campo]: valor }, motivo });
    if (onRefresh) onRefresh();
  };
  const saveDevField = async (campo, valor, motivo) => {
    // Registrar motivo en la auditoría vía nota antes del PATCH.
    await saasApi.patch(`/saas/dispositivos/${selected.dispositivo?.id}`,
      { [campo]: valor, motivo });
    if (onRefresh) onRefresh();
  };
  const [planes, setPlanes] = useState([]);
  // Iter52.58 · Rama 28 · diálogo «Añadir servicio».
  const [servicioOpen, setServicioOpen] = useState(false);
  const [clientesOpts, setClientesOpts] = useState([]);
  React.useEffect(() => {
    (async () => {
      try {
        const r = await saasApi.get('/saas/suscripciones/planes-geotab-disponibles');
        setPlanes((r.data?.items || []).filter(p => p.en_catalogo));
      } catch { /* silencioso */ }
      // Iter52.70 · G3 · `/saas/clientes` NO existe (404 silencioso): la
      // lista de «Añadir servicio» se quedaba vacía. El endpoint real es
      // `/saas/clientes-operativos` y su page_size máximo es 200.
      const URL_CLIENTES = '/saas/clientes-operativos?activo=true&page_size=200';
      try {
        const r = await saasApi.get(URL_CLIENTES);
        const items = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        setClientesOpts(items.map(c => ({ value: c.id, label: `${c.razon_social}${c.cif ? ' · ' + c.cif : ''}` })));
      } catch (e) {
        console.warn('No se pudo cargar la lista de clientes:', URL_CLIENTES, e);
      }
    })();
  }, [selected?.id]);
  const s = selected;
  const d = s.dispositivo || {};
  const p = s.precio_actual || (s.precios || [])[0] || {};
  // Iter52.66 · E2 · El precio que rige HOY (`*_hoy` del detalle). Con un
  // cambio programado, `precio_actual` es el tramo FUTURO: no vale para
  // pintar «precio de hoy». `precio_actual` se deja intacto (bloqueo).
  const tarifaActivaHoy = s.tarifa_activa_hoy ?? p.tarifa_activa;
  const tarifaSuspendidaHoy = s.tarifa_suspendida_hoy ?? p.tarifa_suspendida;
  const per = s.periodo || {};
  const cliFac = s.cliente_facturacion?.razon_social;
  const cliSrv = s.cliente_servicio?.razon_social;
  const idPrincipal = d.matricula || d.serial_geotab || d.id_pack_origen || 'Sin identificar';
  const bloqueantes = s.campos_bloqueantes || [];
  const necesarios = s.campos_necesarios || [];
  const motivos = s.motivos_bloqueantes || [];
  const bloqueada = !!s.bloqueada_facturacion;
  const puedeReconstruir = s.reconstruccion_periodo?.reconstruible === true;

  // Iter52.20 · Mensaje legible por motivo + botón que lo resuelve.
  const MOTIVO_TEXTO = {
    PRECIO_ACTIVA_CERO: 'Precio activo a 0 €',
    PRECIO_ACTIVA_NEGATIVO: 'Precio activo negativo',
    PRECIO_SUSPENDIDA_NEGATIVO: 'Precio suspendida negativo',
    SIN_PRECIO_VIGENTE: 'Sin precio vigente en el mes',
    SIN_PERIODO_ACTIVO: 'Sin periodo de cobro activo',
    FECHA_LIMITE_COBRO_PASADA: 'Fecha límite de cobro pasada',
    SIN_FECHA_INICIO_COBRO: 'Falta fecha de inicio de cobro',
    FECHA_FIN_COBRO_PASADA: 'Fecha fin de cobro en el pasado',
    FUERA_DE_VENTANA_FACTURABLE: 'Fuera de ventana facturable en el mes',
  };
  const RESUELVE_CON = {
    PRECIO_ACTIVA_CERO: 'precio-inicial',
    PRECIO_ACTIVA_NEGATIVO: 'precio',
    PRECIO_SUSPENDIDA_NEGATIVO: 'precio',
    SIN_PRECIO_VIGENTE: 'precio-inicial',
    SIN_PERIODO_ACTIVO: 'periodo',
    FECHA_LIMITE_COBRO_PASADA: 'datos',
    SIN_FECHA_INICIO_COBRO: 'datos',
    FECHA_FIN_COBRO_PASADA: 'datos',
    FUERA_DE_VENTANA_FACTURABLE: 'datos',
  };
  const motivoPrincipal = motivos[0];
  const textoMotivo = motivoPrincipal ? (MOTIVO_TEXTO[motivoPrincipal] || motivoPrincipal) : null;
  const resuelveCon = motivoPrincipal ? RESUELVE_CON[motivoPrincipal] : null;

  // Mapa campo → tono para calcular puntos en pestañas
  const tonoMap = useMemo(() => {
    const m = {};
    for (const k of bloqueantes) m[k] = 'RED';
    for (const k of necesarios) if (!m[k]) m[k] = 'AMBER';
    return m;
  }, [bloqueantes, necesarios]);

  const camposPorPestana = {
    operativa: ['tipo_periodo', 'estado_periodo', 'fecha_inicio_cobro', 'fecha_limite_cobro', 'fecha_fin_cobro', 'fecha_activacion_geotab'],
    negocio: ['precio_activo', 'precio_suspendido'],
    sistema: [],
    historico: [],
  };
  const dotsPorPestana = Object.fromEntries(TABS.map(t => [t.k, domTone(camposPorPestana[t.k], tonoMap)]));

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="ficha-suscripcion">
      <div className="bg-slate-50 rounded-2xl shadow-xl max-w-6xl w-full max-h-[92vh] overflow-hidden flex flex-col">
        {/* Cabecera fija común */}
        <div className="bg-white border-b sticky top-0 z-10">
          <div className="px-5 py-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 flex-wrap">
              {fromControl && <button data-testid="btn-volver-control" className="btn-secondary text-xs" onClick={backToControl}>← Volver a Control</button>}
              <span className="font-black text-lg" data-testid="ficha-identificador">{idPrincipal}</span>
              <span className="text-sm text-slate-500">· {cliFac || '—'}</span>
              <Badge tone={statusTone(s.estado)}>{s.estado}</Badge>
              {d.estado && <Badge tone={statusTone(d.estado)}>{d.estado}</Badge>}
              {bloqueada && <span data-testid="ficha-badge-bloqueada" className="inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-wide bg-red-600 text-white rounded px-2 py-1"><AlertTriangle className="w-3 h-3"/>Bloqueada{textoMotivo?` · ${textoMotivo}`:' para facturación'}</span>}
            </div>
            <button data-testid="ficha-btn-cerrar" onClick={onClose} className="text-slate-500 hover:text-slate-900"><X className="w-5 h-5"/></button>
          </div>
          <div className="px-5 pb-2 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <div><span className="text-slate-500">Servicio:</span> <b>{s.tipo_servicio_snapshot || '—'}</b></div>
            <div><span className="text-slate-500">Cliente:</span> <b>{cliFac || '—'}</b></div>
            <div><span className="text-slate-500">Inicio de cobro:</span> <b>{fmtDate(s.fecha_inicio_cobro)}</b></div>
            <div><span className="text-slate-500">ID Geotab / PACK:</span> <b className="font-mono">{d.serial_geotab || d.id_pack_origen || '—'}</b></div>
          </div>
          <div className="px-5 flex gap-1 border-t">
            {TABS.map(t => (
              <button
                key={t.k}
                data-testid={`ficha-tab-${t.k}`}
                onClick={() => setTab(t.k)}
                className={`px-4 py-2 text-sm font-bold border-b-2 flex items-center ${tab === t.k ? 'border-[#0a6640] text-[#0a6640]' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
              >
                {t.label}<TabDot tone={dotsPorPestana[t.k]} />
              </button>
            ))}
          </div>
        </div>

        {/* Contenido */}
        <div className="p-5 overflow-auto flex-1">
          {tab === 'operativa' && (
            <div data-testid="ficha-tab-operativa">
              <h4 className="text-xs uppercase tracking-wide text-slate-500 font-bold mb-2">Suscripción y período</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                <FieldCard label="Estado suscripción" k="estado_saas" bloqueantes={bloqueantes} necesarios={necesarios} value={s.estado} />
                <FieldCard label="Tipo de periodo" testid="card-tipo_periodo" k="tipo_periodo" bloqueantes={bloqueantes} necesarios={necesarios} value={per.tipo} ausente={!per.tipo} />
                <FieldCard label="Estado del periodo" testid="card-estado_periodo" k="estado_periodo" bloqueantes={bloqueantes} necesarios={necesarios} value={per.activo === undefined ? '—' : (per.activo ? 'ACTIVO' : 'CERRADO')} ausente={!per.activo && per.activo !== false} />
                <FieldCard label="Meses prepagados" k="meses_prepagados" bloqueantes={bloqueantes} necesarios={necesarios} value={per.meses_prepagados || '—'} />
                {/* Iter52.42 · Fechas del período activo (canónicas: `saas_periodos_cobro`).
                    Complementan las del contrato (`s.fecha_inicio_cobro` / `s.fecha_fin_cobro`)
                    con lo que realmente está siendo facturado por el motor. */}
                <FieldCard label="Inicio período activo" testid="card-periodo_activo_inicio" value={fmtDate(per.fecha_inicio)} ausente={!per.fecha_inicio} />
                <FieldCard label="Fin período activo" testid="card-periodo_activo_fin" value={fmtDate(per.fecha_fin)} ausente={!per.fecha_fin && per.tipo === 'PREPAGO'} />
                <FieldCard label="Inicio de cobro" k="fecha_inicio_cobro" bloqueantes={bloqueantes} necesarios={necesarios} value={fmtDate(s.fecha_inicio_cobro)} ausente={!s.fecha_inicio_cobro} />
                <FieldCard label={s.meses_cortesia != null ? "Fin de cortesía (inicio de cobro)" : "Fecha límite de cobro"} k="fecha_limite_cobro" bloqueantes={bloqueantes} necesarios={necesarios} value={fmtDate(s.fecha_limite_cobro)} ausente={!s.fecha_limite_cobro} />
                <FieldCard label="Fin de cobro" k="fecha_fin_cobro" bloqueantes={bloqueantes} necesarios={necesarios} value={fmtDate(s.fecha_fin_cobro)} ausente={!s.fecha_fin_cobro} />
                <FieldCard label="Fecha activación Geotab" k="fecha_activacion_geotab" bloqueantes={bloqueantes} necesarios={necesarios} value={`${fmtDate(d.fecha_activacion_geotab)}${d.fecha_activacion_origen === 'ESTIMADA_INSTALACION' ? ' (estimada)' : ''}`} ausente={!d.fecha_activacion_geotab} />
              </div>

              <h4 className="text-xs uppercase tracking-wide text-slate-500 font-bold mt-6 mb-2">Dispositivo y despliegue</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                <FieldCard label="Estado del dispositivo" value={d.estado} />
                <FieldCard label="Familia" value={s.familia_dispositivo?.nombre || s.familia_dispositivo?.codigo} ausente={!s.familia_dispositivo} />
                <FieldCard label="Fecha de instalación" value={fmtDate(d.fecha_instalacion)} ausente={!d.fecha_instalacion} />
                <FieldCard label="Fecha comunicación Geotab" value={fmtDate(d.fecha_comunicacion_geotab)} ausente={!d.fecha_comunicacion_geotab} />
              </div>

              {/* Acciones separadas: Operar / Corregir */}
              <div className="mt-8 border-t pt-4" data-testid="ficha-acciones">
                <p className="text-xs uppercase tracking-wide text-slate-500 font-bold mb-2">Operar</p>
              {/* Iter52.20 · Operar · destaca el botón que resuelve el
                  bloqueo actual (si lo hay); atenúa "Dar de baja" para
                  que la acción peligrosa no compita visualmente. */}
              <div className="flex gap-2 flex-wrap">
                {s.estado === 'ACTIVA' && <button data-testid="ficha-btn-suspender" className="btn-secondary" onClick={() => onOperar('suspender')}>Suspender</button>}
                {s.estado === 'SUSPENDIDA' && <button data-testid="ficha-btn-reactivar" className="btn-primary" onClick={() => onOperar('reactivar')}>Reactivar</button>}
                {resuelveCon === 'precio-inicial'
                  ? <button data-testid="ficha-btn-asignar-precio-inicial"
                            className="btn-primary ring-2 ring-red-300 shadow-lg animate-pulse-slow"
                            onClick={onAsignarPrecioInicial}
                            title="Rellena el hueco de precio desde el inicio de cobro (corrección de dato de migración)">
                      Asignar precio inicial · resuelve el bloqueo
                    </button>
                  : <button data-testid="ficha-btn-cambiar-precio"
                            className={resuelveCon==='precio'?"btn-primary ring-2 ring-red-300 shadow-lg animate-pulse-slow":"btn-secondary"}
                            onClick={onCambiarPrecio}
                            title={resuelveCon==='precio'?'Resuelve el bloqueo actual':undefined}>
                      Cambiar precio{resuelveCon==='precio' && ' · resuelve el bloqueo'}
                    </button>}
                <button data-testid="ficha-btn-cambiar-suscripcion" className="btn-secondary" onClick={onCambiarSuscripcion} disabled={s.estado !== 'ACTIVA' && s.estado !== 'SUSPENDIDA'}>Cambiar suscripción</button>
                {/* Iter52.58 · Rama 28 · punto 4 · solo desde una PRINCIPAL viva. */}
                {!s.suscripcion_principal_id && s.estado !== 'BAJA' && (
                  <button data-testid="ficha-btn-anadir-servicio" className="btn-secondary"
                          title="Añadir un SaaS que no va sobre Geotab (hotline y similares) a este equipo"
                          onClick={() => setServicioOpen(true)}>
                    Añadir servicio
                  </button>
                )}
                {/* Iter52.53 · A4 · Registrar renovación facturada fuera de la app.
                    Aplica a PREPAGOs (crea el periodo siguiente con marca FUERA_APP,
                    sin línea, motivo obligatorio). */}
                {s.estado !== 'BAJA' && (s.periodo_activo?.tipo === 'PREPAGO' || !s.periodo_activo) && (
                  <button data-testid="ficha-btn-renovacion-fuera-app"
                          className="btn-secondary"
                          title="Registrar una renovación que se facturó fuera de la app (crea el periodo siguiente sin generar línea)"
                          onClick={() => onRenovacionFueraApp && onRenovacionFueraApp()}>
                    Renovación facturada fuera de la app
                  </button>
                )}
                {s.estado === 'BAJA'
                  ? <button data-testid="ficha-btn-reactivar-sensible" className="btn-primary bg-amber-600 hover:bg-amber-700" onClick={onReactivarSensible}>Reactivar desde BAJA (sensible)</button>
                  : <button data-testid="ficha-btn-baja"
                            className="text-xs text-slate-500 underline hover:text-red-700 self-center ml-auto"
                            onClick={() => onOperar('baja')}>
                      Dar de baja
                    </button>}
              </div>
                <p className="text-xs uppercase tracking-wide text-slate-500 font-bold mb-2">Corregir</p>
                <div className="flex gap-2 flex-wrap">
                  <button data-testid="ficha-btn-corregir-datos"
                          className={resuelveCon==='datos'?"btn-primary ring-2 ring-red-300 shadow-lg":"btn-secondary"}
                          onClick={onCorregirDatos}>
                    Corregir datos{resuelveCon==='datos' && ' · resuelve el bloqueo'}
                  </button>
                  <button data-testid="ficha-btn-corregir-periodo"
                          className={resuelveCon==='periodo'?"btn-primary ring-2 ring-red-300 shadow-lg":"btn-secondary"}
                          onClick={onCorregirPeriodo}>
                    Corregir período{resuelveCon==='periodo' && ' · resuelve el bloqueo'}
                  </button>
                  <button data-testid="ficha-btn-corregir-datos-dispositivo" className="btn-secondary" onClick={() => onCorregirDatosDispositivo(d)}>Corregir datos del dispositivo</button>
                  <button data-testid="ficha-btn-corregir-estado-dispositivo" className="btn-secondary" onClick={() => onCorregirEstadoDispositivo(d)}>Corregir estado del dispositivo</button>
                  {puedeReconstruir && <button data-testid="ficha-btn-reconstruir-periodo" className="btn-primary bg-teal-700 hover:bg-teal-800" onClick={onReconstruirPeriodo}><RefreshCcw className="w-3 h-3 inline mr-1"/>Reconstruir período</button>}
                </div>
                <p className="text-[11px] text-slate-500 mt-3">Todas las acciones respetan permisos, requieren motivo, ejecutan las validaciones canónicas (cascadas, prefacturas cerradas, transiciones permitidas) y quedan auditadas. La corrección del estado del dispositivo no modifica suscripción, precios, periodos ni frecuencia.</p>
                {servicioOpen && <AnadirServicioDialog selected={selected}
                  onCancel={() => setServicioOpen(false)}
                  onDone={() => { setServicioOpen(false); if (onRefresh) onRefresh(); }} />}
              </div>
            </div>
          )}

          {tab === 'negocio' && (
            <div data-testid="ficha-tab-negocio">
              <h4 className="text-xs uppercase tracking-wide text-slate-500 font-bold mb-2">Facturación</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                <FieldCard label="Cliente de facturación" value={cliFac} ausente={!cliFac} />
                <InlineEditRow label="Cliente de servicio" value={cliSrv} kind="select" options={clientesOpts} testid="inline-cliente-servicio"
                  hint="Cambia el cliente que recibe el servicio (no el que paga la factura)."
                  save={(v, m) => saveSusField('cliente_servicio_id', v || null, m)}/>
                <FieldCard label="Precio activo" testid="card-precio_activo" k="precio_activo" bloqueantes={bloqueantes} necesarios={necesarios} value={money(tarifaActivaHoy)} ausente={tarifaActivaHoy == null}
                  extra={<ProximoPrecio hoy={tarifaActivaHoy} proxima={s.tarifa_activa_proxima} desde={s.vigente_desde_proxima} />} />
                <FieldCard label="Precio suspendido" testid="card-precio_suspendido" k="precio_suspendido" bloqueantes={bloqueantes} necesarios={necesarios} value={money(tarifaSuspendidaHoy)} ausente={tarifaSuspendidaHoy == null}
                  extra={<ProximoPrecio hoy={tarifaSuspendidaHoy} proxima={s.tarifa_suspendida_proxima} desde={s.vigente_desde_proxima} />} />
                <FieldCard label="Coste" testid="card-coste" value={money(s.coste_geotab_actual)} ausente={s.coste_geotab_actual == null} />
                <FieldCard label="Tarifa mensual equivalente" value={money(p.tarifa_mensual_equivalente ?? tarifaActivaHoy)} />
                <FieldCard label="Frecuencia" value={per.frecuencia_origen || p.frecuencia_origen} ausente={!per.frecuencia_origen && !p.frecuencia_origen} />
                <FieldCard label="Meses prepagados" value={per.meses_prepagados || '—'} />
              </div>
              <h4 className="text-xs uppercase tracking-wide text-slate-500 font-bold mb-2 mt-5">Contrato y notas · edición en línea</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <InlineEditRow label="Referencia de contrato" value={s.referencia_contrato} testid="inline-referencia-contrato"
                  placeholder="p.ej. CONT-2024-0182"
                  save={(v, m) => saveSusField('referencia_contrato', v || null, m)}/>
                <InlineEditRow label="Comentarios" value={s.comentarios} kind="textarea" testid="inline-comentarios"
                  placeholder="Notas internas que quieras dejar sobre esta suscripción."
                  save={(v, m) => saveSusField('comentarios', v || null, m)}/>
              </div>
              {(() => {
                // Iter52.6 · Agrupar vacíos NO bloqueantes de Negocio en un
                // bloque "Pendientes de completar".
                const negocio = [
                  ["Importe original", per.importe_prepago_original ?? p.importe_periodo_original],
                ];
                const pend = negocio.filter(([, v]) => v == null || v === '');
                if (!pend.length) return null;
                return (
                  <div className="mt-5" data-testid="ficha-negocio-pendientes">
                    <p className="text-xs uppercase tracking-wide text-slate-500 font-bold mb-2">Pendientes de completar</p>
                    <div className="text-xs text-slate-500 flex flex-wrap gap-x-4 gap-y-1">
                      {pend.map(([k]) => <span key={k}>• {k}</span>)}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {tab === 'sistema' && (
            <div data-testid="ficha-tab-sistema">
              <h4 className="text-xs uppercase tracking-wide text-slate-500 font-bold mb-2">Identificadores internos y códigos</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                <FieldCard label="ID interno suscripción" value={s.id} mono />
                <FieldCard label="Producto SaaS" value={s.producto_saas?.nombre || s.tipo_servicio_snapshot} />
                <FieldCard label="Código SaaS" value={s.producto_saas?.sku || s.producto_saas_codigo_origen} mono />
                {/* Iter52.63 · P6 · «Plan Geotab propuesto (metadata)» se
                    oculta: el canónico es `plan_geotab_codigo_origen` y la
                    metadata ya no se escribe. La columna PLAN_GEOTAB sigue en
                    el esquema del Excel por compatibilidad. */}
                <InlineEditRow label="Plan Geotab · CANÓNICO (casa con catálogo)" value={s.plan_geotab_codigo_origen} mono kind="select"
                  options={planes.map(p => ({ value: p.codigo, label: p.codigo }))}
                  testid="inline-plan-geotab"
                  hint="Al cambiar de plan se retira automáticamente el sello CATALOGO_GEOTAB. El próximo Actualizar costes revalidará contra el catálogo vigente."
                  save={(v, m) => saveSusField('plan_geotab_codigo_origen', v || null, m)}/>
                <InlineEditRow label="ID Geotab (serial)" value={d.serial_geotab} mono testid="inline-serial-geotab"
                  save={(v, m) => saveDevField('serial_geotab', v || null, m)}/>
                <FieldCard label="ID PACK" value={d.id_pack_origen} mono />
                <InlineEditRow label="VIN" value={d.vin} mono testid="inline-vin"
                  save={(v, m) => saveDevField('vin', v || null, m)}/>
                <InlineEditRow label="Matrícula" value={d.matricula} testid="inline-matricula"
                  save={(v, m) => saveDevField('matricula', v || null, m)}/>
                <InlineEditRow label="Régimen" value={d.regimen} kind="select"
                  options={[{ value: 'VENTA', label: 'VENTA' }, { value: 'RENTING', label: 'RENTING' }]}
                  testid="inline-regimen"
                  save={(v, m) => saveDevField('regimen', v || null, m)}/>
                <FieldCard label="Asociación STOCK" value={d.import_maestro_stock_asociado || d.serial_geotab ? 'ASOCIADO' : 'SIN ASOCIAR'} />
                <FieldCard label="ID interno dispositivo" value={d.id} mono />
              </div>
            </div>
          )}

          {tab === 'historico' && (
            <div className="space-y-2 max-h-[65vh] overflow-auto" data-testid="ficha-tab-historico">
              {(s.historico || []).length
                ? (s.historico || []).map(e => (
                    <div key={e.id} className="text-sm border rounded-lg p-3 bg-white">
                      <div className="flex justify-between items-start gap-3"><b>{e.tipo_evento}</b><span className="text-xs text-slate-500">{fmtDate(e.fecha_efectiva)}</span></div>
                      {e.motivo && <p className="text-xs text-slate-600 mt-1">{e.motivo}</p>}
                    </div>
                  ))
                : <p className="text-sm text-slate-400 py-6 text-center">Sin eventos registrados.</p>}
            </div>
          )}
        </div>

        {/* Leyenda al pie */}
        <Leyenda />
      </div>
    </div>
  );
}
